import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  GitMerge, RefreshCw, Loader2, AlertTriangle, Building2, 
  User, Mail, Phone, ArrowRight, Check, X, Search
} from 'lucide-react';
import * as api from '../../services/api';

const DuplicatesTab = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Merge dialog state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [mergeDirection, setMergeDirection] = useState('1to2'); // '1to2' or '2to1'
  const [merging, setMerging] = useState(false);

  const loadDuplicates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.findDuplicateEmpresas();
      setDuplicates(response.data?.duplicates || []);
    } catch (err) {
      console.error('Error loading duplicates:', err);
      setError('Error cargando duplicados: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDuplicates();
  }, [loadDuplicates]);

  const openMergeDialog = (duplicate) => {
    setSelectedDuplicate(duplicate);
    setMergeDirection('1to2');
    setShowMergeDialog(true);
  };

  const handleMerge = async () => {
    if (!selectedDuplicate) return;
    
    const sourceId = mergeDirection === '1to2' 
      ? selectedDuplicate.empresa1.id 
      : selectedDuplicate.empresa2.id;
    const targetId = mergeDirection === '1to2' 
      ? selectedDuplicate.empresa2.id 
      : selectedDuplicate.empresa1.id;
    
    try {
      setMerging(true);
      const response = await api.mergeEmpresas(sourceId, targetId);
      alert(`Fusión exitosa: ${JSON.stringify(response.data.summary, null, 2)}`);
      setShowMergeDialog(false);
      setSelectedDuplicate(null);
      loadDuplicates();
    } catch (err) {
      console.error('Error merging:', err);
      alert('Error al fusionar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMerging(false);
    }
  };

  const dismissDuplicate = (idx) => {
    setDuplicates(prev => prev.filter((_, i) => i !== idx));
  };

  const getReasonBadge = (reason) => {
    const colors = {
      'nombre similar': 'bg-blue-100 text-blue-800',
      'mismo email': 'bg-green-100 text-green-800',
      'mismo teléfono': 'bg-purple-100 text-purple-800'
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-orange-500" />
            Duplicados y Fusión
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Detecta y fusiona empresas duplicadas automáticamente
          </p>
        </div>
        <Button onClick={loadDuplicates} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Buscar Duplicados
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{duplicates.length}</p>
              <p className="text-sm text-gray-500">Duplicados detectados</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Search className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {duplicates.filter(d => d.reason === 'nombre similar').length}
              </p>
              <p className="text-sm text-gray-500">Por nombre</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {duplicates.filter(d => d.reason === 'mismo email').length}
              </p>
              <p className="text-sm text-gray-500">Por email</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Duplicates List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Buscando duplicados...</span>
        </div>
      ) : duplicates.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-700">No se encontraron duplicados</h3>
            <p className="text-sm text-gray-500 mt-1">
              Tu base de datos está limpia de empresas duplicadas
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {duplicates.map((dup, idx) => (
            <Card key={idx} className="border-l-4 border-l-orange-400">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    {/* Empresa 1 */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{dup.empresa1.name}</span>
                      </div>
                      {dup.empresa1.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="h-3 w-3" />
                          {dup.empresa1.email}
                        </div>
                      )}
                      {dup.empresa1.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-3 w-3" />
                          {dup.empresa1.phone}
                        </div>
                      )}
                    </div>
                    
                    {/* Empresa 2 */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{dup.empresa2.name}</span>
                      </div>
                      {dup.empresa2.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="h-3 w-3" />
                          {dup.empresa2.email}
                        </div>
                      )}
                      {dup.empresa2.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-3 w-3" />
                          {dup.empresa2.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <Badge className={getReasonBadge(dup.reason)}>
                      {dup.reason}
                    </Badge>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => dismissDuplicate(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => openMergeDialog(dup)}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        <GitMerge className="h-4 w-4 mr-1" />
                        Fusionar
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-orange-500" />
              Fusionar Empresas
            </DialogTitle>
            <DialogDescription>
              Selecciona cuál empresa mantener. La otra será fusionada y eliminada.
            </DialogDescription>
          </DialogHeader>
          
          {selectedDuplicate && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-3 rounded-lg border-2 ${mergeDirection === '2to1' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                  <p className="font-medium text-sm">{selectedDuplicate.empresa1.name}</p>
                  <p className="text-xs text-gray-500">{selectedDuplicate.empresa1.email}</p>
                  {mergeDirection === '2to1' && (
                    <Badge className="mt-2 bg-green-100 text-green-800">Mantener</Badge>
                  )}
                  {mergeDirection === '1to2' && (
                    <Badge className="mt-2 bg-red-100 text-red-800">Eliminar</Badge>
                  )}
                </div>
                
                <ArrowRight className="h-6 w-6 text-gray-400" />
                
                <div className={`p-3 rounded-lg border-2 ${mergeDirection === '1to2' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                  <p className="font-medium text-sm">{selectedDuplicate.empresa2.name}</p>
                  <p className="text-xs text-gray-500">{selectedDuplicate.empresa2.email}</p>
                  {mergeDirection === '1to2' && (
                    <Badge className="mt-2 bg-green-100 text-green-800">Mantener</Badge>
                  )}
                  {mergeDirection === '2to1' && (
                    <Badge className="mt-2 bg-red-100 text-red-800">Eliminar</Badge>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Dirección de fusión:</label>
                <Select value={mergeDirection} onValueChange={setMergeDirection}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1to2">
                      Fusionar "{selectedDuplicate.empresa1.name}" → "{selectedDuplicate.empresa2.name}"
                    </SelectItem>
                    <SelectItem value="2to1">
                      Fusionar "{selectedDuplicate.empresa2.name}" → "{selectedDuplicate.empresa1.name}"
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                <p className="font-medium text-yellow-800">⚠️ Esta acción:</p>
                <ul className="list-disc list-inside text-yellow-700 mt-1 space-y-1">
                  <li>Moverá todos los contactos a la empresa destino</li>
                  <li>Moverá todas las notas a la empresa destino</li>
                  <li>Actualizará las tareas y deals asociados</li>
                  <li>Eliminará la empresa origen</li>
                  <li><strong>No se puede deshacer</strong></li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleMerge}
              disabled={merging}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {merging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Fusionando...
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4 mr-2" />
                  Confirmar Fusión
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DuplicatesTab;
